import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { Button, message, Space } from 'antd';
import moment from 'moment';
import { HiChevronRight } from 'react-icons/hi2';
import Linkify from 'react-linkify';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '../../../../context/ThemeProvider';
import { useAppSelector } from '../../../../shared/hooks';
import { nFormat, timeAgo } from '../../../../shared/utils';
import {
  IChannelPoll,
  IPostedBy,
  IPostPollOption,
} from '../../../../types/communityTypes';
import { Onboarding } from '../../../../types/userTypes';

type Props = {
  poll: Partial<IChannelPoll>;
  postedBy: Partial<IPostedBy>;
  vote: (arg0?: string) => Promise<void>;
  undoVote: (arg0?: string) => Promise<void>;
  pollVotesPerOption: { [key: string]: number };
  totalPollVotes: number;
  votedOption?: string | false;
  setSelectedOption: Dispatch<SetStateAction<string>>;
  setShowPollVotedUserList: Dispatch<SetStateAction<boolean>>;
  style?: React.CSSProperties;
};

const CommunityPostPoll: React.FC<Props> = ({
  poll,
  postedBy,
  pollVotesPerOption,
  totalPollVotes,
  votedOption,
  vote,
  undoVote,
  setSelectedOption,
  setShowPollVotedUserList,
  style,
}) => {
  const navigate = useNavigate();
  const userDetails = useAppSelector((state) => state.user);
  const { colors } = useTheme();
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(false);

  const viewVotedByUsersList = () => {};

  const isUserAllowViewVote = useMemo(() => {
    return (
      userDetails?.id === postedBy?._id ||
      poll?.isResultVisibleToMembers ||
      userDetails?.type === Onboarding.CREATOR_COMPLETED
    );
  }, [
    poll?.isResultVisibleToMembers,
    postedBy?._id,
    userDetails?.id,
    userDetails?.type,
  ]);

  const handleClickOnVote = async (option: IPostPollOption) => {
    if (moment(poll.pollEndAt || new Date()).isAfter(moment())) {
      setLoading(true);
      await vote(option.optionId);
      setLoading(false);
    } else {
      message.error('Poll has ended');
    }
  };

  const votingPerObj: { [key: string]: string } = useMemo(() => {
    const perObj = (poll?.pollOptions || []).reduce((acc, eachOptio) => {
      const optionPerVote = pollVotesPerOption?.[eachOptio?.optionId] || 0;
      const totalVotes = totalPollVotes || 0;
      const per = totalVotes > 0 ? (optionPerVote / totalVotes) * 100 : 0;
      Object.assign(acc, { [eachOptio?.optionId]: per.toFixed(2) });
      return acc;
    }, {});
    return perObj || {};
  }, [poll?.pollOptions, pollVotesPerOption, totalPollVotes]);

  // console.log(poll, votingPerObj, totalPollVotes, pollVotesPerOption);
  return (
    <div
      className="community-feed-poll_contailer"
      style={showResult ? style : {}}>
      <div className="community-feed-poll_contailer_wrapper">
        <Linkify>
          <h2>{poll?.question?.trim()}</h2>
        </Linkify>
        {showResult ? (
          <div className="community-feed-poll_contailer_poll-answer-wrapper">
            {(poll?.pollOptions || []).map((option) => (
              <div
                key={option.optionId}
                onClick={() => {
                  const search = new URLSearchParams(window.location.search);
                  search.append('votedUserList', 'true');
                  search.append('selectedOption', option.optionId);
                  console.log('clicked happed');
                  setSelectedOption(option.optionId);
                  setShowPollVotedUserList(true);
                  navigate({
                    search: search.toString(),
                  });
                }}
                className="community-feed-poll_contailer_poll-answer-wrapper_poll-answer">
                <div
                  className="community-feed-poll_contailer_poll-answer-wrapper_poll-answer_option-progress"
                  style={{
                    width: `${votingPerObj?.[option?.optionId] || 0}%`,
                  }}
                />
                <div className="community-feed-poll_contailer_poll-answer-wrapper_poll-answer_option-text">
                  {option.optionContent?.trim()}
                </div>
                <div className="community-feed-poll_contailer_poll-answer-wrapper_poll-answer_option-per">
                  {votingPerObj?.[option?.optionId] || 0}% <HiChevronRight />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="community-feed-poll_contailer_poll-option-wrapper">
            {(poll?.pollOptions || []).map((option) => (
              <Button
                key={option.optionId}
                className={`siteBtn noStyle community-feed-poll_contailer_poll-option-wrapper_poll-option ${
                  votedOption === option.optionId ? 'active' : ''
                }`}
                block
                disabled={
                  loading ||
                  (Boolean(votedOption) && votedOption !== option.optionId)
                }
                loading={loading}
                onClick={() => handleClickOnVote(option)}>
                {option.optionContent?.trim()}
              </Button>
            ))}
          </div>
        )}
        <Space
          align="center"
          split={<span>•</span>}
          size={4}
          style={{ fontSize: 12, color: colors.TEXT3 }}>
          <div onClick={viewVotedByUsersList}>
            {nFormat(totalPollVotes || 0, 999)} votes
          </div>
          {moment().isBefore(poll?.pollEndAt) ? (
            <div>{timeAgo(poll?.pollEndAt)} left</div>
          ) : null}
          {poll?.allowVoteChange &&
          votedOption &&
          moment().isBefore(poll?.pollEndAt) ? (
            <Button
              className="siteBtn siteBtnLink sm"
              onClick={() => undoVote(votedOption as string)}>
              Undo
            </Button>
          ) : null}
          {isUserAllowViewVote ? (
            <Button
              className="siteBtn siteBtnLink blue sm"
              onClick={() => setShowResult((prev) => !prev)}>
              {showResult ? 'Hide' : 'View'} result
            </Button>
          ) : null}
        </Space>
      </div>
    </div>
  );
};

export default CommunityPostPoll;
